import { useEffect, useState } from 'react';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';
import { AgentDefinition, AgentOptions, PromptMode, ModelInfo } from '../types';

const EMPTY_OPTIONS: AgentOptions = { promptModes: [], supportedModels: [], defaultModel: null };

// Module-level cache: agentId → AgentOptions (lives for the session)
const optionsCache = new Map<string, AgentOptions>();

export interface UseAgentOptionsResult {
  options: AgentOptions;
  loading: boolean;
  promptsError: string | null;
  modelsError: string | null;
}

export function useAgentOptions(agent: AgentDefinition | null): UseAgentOptionsResult {
  const getToken = useAccessToken();
  const [options, setOptions] = useState<AgentOptions>(EMPTY_OPTIONS);
  const [loading, setLoading] = useState(false);
  const [promptsError, setPromptsError] = useState<string | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);

  useEffect(() => {
    if (!agent) {
      setOptions(EMPTY_OPTIONS);
      return;
    }

    // Return cached result immediately without fetching
    const cached = optionsCache.get(agent.id);
    if (cached) {
      setOptions(cached);
      return;
    }

    const hasPrompts = !!agent.endpoints?.promptsUrl;
    const hasModels = !!agent.endpoints?.modelsUrl;

    if (!hasPrompts && !hasModels) {
      setOptions(EMPTY_OPTIONS);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setPromptsError(null);
    setModelsError(null);

    async function load() {
      let promptModes: PromptMode[] = [];
      let supportedModels: ModelInfo[] = [];
      let defaultModel: string | null = null;
      let pErr: string | null = null;
      let mErr: string | null = null;

      await Promise.all([
        hasPrompts
          ? fetchApi<any>(`/api/agents/${agent!.id}/prompts`, getToken)
              .then((data) => {
                if (Array.isArray(data)) {
                  promptModes = data;
                } else if (data && typeof data === 'object') {
                  promptModes = data.prompts ?? data.modes ?? data.prompt_modes ?? data.data ?? [];
                }
              })
              .catch((e) => { pErr = e?.message ?? 'Failed to load prompt modes'; })
          : Promise.resolve(),

        hasModels
          ? fetchApi<any>(`/api/agents/${agent!.id}/models`, getToken)
              .then((data) => {
                if (Array.isArray(data)) {
                  supportedModels = data;
                } else {
                  supportedModels = data.models ?? data.data ?? [];
                  defaultModel = data.default ?? data.default_model ?? null;
                }
              })
              .catch((e) => { mErr = e?.message ?? 'Failed to load models'; })
          : Promise.resolve(),
      ]);

      if (cancelled) return;

      const result: AgentOptions = { promptModes, supportedModels, defaultModel };
      optionsCache.set(agent!.id, result);
      setOptions(result);
      setPromptsError(pErr);
      setModelsError(mErr);
      setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id]);

  return { options, loading, promptsError, modelsError };
}
